import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../Helpers/config.json";
import { formatAmount } from "../../Utils/formatAmount";
import { titleCase } from "../../Utils/titleCaseFunction";
import ReusableDashboardHeader from "../../Containers/Dasboard/ReusableDashboardHeader";
import { BackButton } from "../../Utils/FormInputs/BackButton";
import Label from "../../Utils/FormInputs/Label";
import { Container } from "../../Utils/styledComponents";
import { MuiPaper } from "../../Utils/Tables/CustomTable";
import {
  LinearProgress,
  TextField,
  InputAdornment,
  Button,
  Card,
  CardContent,
  Grid,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import PharmacySnackbar from "../../Utils/Snackbars/SnackbarUtil";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../Redux/Actions/SnackbarActions/SnackbarActions";

const SupplierLedgerDetailReport = ({ actions, snackbars, match }) => {
  const { supplier_id } = match.params;

  // Safe initial state
  const [ledger, setLedger] = useState({
    supplier: { name: "", mobile: "", address: "", id: "" },
    period: { from: "", to: "" },
    opening_balance: 0,
    closing_balance: 0,
    transactions: [],
    summary: {
      total_purchases: 0,
      total_credit_purchases: 0,
      total_cash_purchases: 0,
      total_payments: 0,
      net_payable: 0,
    },
  });

  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Set default date range: last 90 days
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setToDate(today);
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    setFromDate(ninetyDaysAgo.toISOString().split("T")[0]);
  }, []);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${config.epharmUrl}/Creport/supplier-ledger/${supplier_id}`,
        {
          params: {
            start_date: fromDate || undefined,
            end_date: toDate || undefined,
          },
        }
      );

      console.log("API Response:", res.data);

      if (res.data.success && res.data.data) {
        const d = res.data.data;

        setLedger({
          supplier: {
            name: d.supplier?.name || "Unknown Supplier",
            mobile: d.supplier?.mobile || "N/A",
            address: d.supplier?.address || "N/A",
            id: d.supplier?.id || supplier_id,
          },
          period: {
            from: d.period?.from || fromDate,
            to: d.period?.to || toDate,
          },
          opening_balance: Number(d.opening_balance) || 0,
          closing_balance: Number(d.closing_balance) || 0,
          transactions: Array.isArray(d.transactions) ? d.transactions : [],
          summary: {
            total_purchases: Number(d.summary?.total_purchases) || 0,
            total_credit_purchases: Number(d.summary?.total_credit_purchases) || 0,
            total_cash_purchases: Number(d.summary?.total_cash_purchases) || 0,
            total_payments: Number(d.summary?.total_payments) || 0,
            net_payable: Number(d.summary?.net_payable) || 0,
          },
        });
      } else {
        actions.snackbarActions.errorResponse("No ledger data received");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      actions.snackbarActions.errorResponse(
        err.response?.data?.message || "Failed to load ledger"
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when dates change
  useEffect(() => {
    if (fromDate && toDate && supplier_id) {
      const delay = setTimeout(fetchLedger, 300);
      return () => clearTimeout(delay);
    }
  }, [fromDate, toDate, supplier_id]);

  // Destructure
  const {
    supplier,
    period,
    opening_balance,
    closing_balance,
    transactions,
    summary,
  } = ledger;

  const { open, message, variant } = snackbars;

  // Search filter
  const filteredTxns = Array.isArray(transactions)
    ? transactions.filter(
        (t) =>
          t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.ref_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.mode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.paid_via?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="journals">
      <ReusableDashboardHeader
        component="Supplier Ledger Report"
        heading="Suppliers"
        subHeading={`${titleCase(supplier.name || "Loading...")} Ledger`}
        link={`/supplier-ledger/${supplier_id}`}
      />

      <PharmacySnackbar
        open={open}
        message={message}
        variant={variant}
        handleCloseBar={() => actions.snackbarActions.hideSnackBar()}
      />

      <Container>
        <div className="mb-4">
          <BackButton to="/managemanufacturer" text="Back to Suppliers" />
        </div>

        {/* Supplier Info Card */}
        <Card className="shadow-sm mb-4 border-start border-warning border-4">
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <h3 className="mb-1 text-warning">
                  {titleCase(supplier.name || "Walk-in Supplier")}
                </h3>
                <p className="text-muted mb-1">
                  Mobile: <strong>{supplier.mobile || "N/A"}</strong> | 
                  Address: {supplier.address || "N/A"}
                </p>
                <small className="text-muted">
                  Supplier ID: <strong>{supplier.id || "-"}</strong>
                </small>
              </Grid>
              <Grid item xs={12} md={4} className="text-md-end">
                <div className="mb-2">
                  <strong>Period:</strong> {period.from} to {period.to}
                </div>
                <h4 className={`mb-0 ${closing_balance > 0 ? "text-danger" : "text-success"}`}>
                  Balance Payable: <strong>{formatAmount(closing_balance)}</strong>
                  {closing_balance === 0 && " (Fully Paid)"}
                </h4>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} className="mb-4">
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-info shadow-sm">
              <CardContent>
                <div className="text-uppercase text-info small mb-1">Opening Balance</div>
                <h4 className="mb-0">{formatAmount(opening_balance)}</h4>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-primary shadow-sm">
              <CardContent>
                <div className="text-uppercase text-primary small mb-1">Total Purchases</div>
                <h4 className="mb-0 text-primary">{formatAmount(summary.total_purchases)}</h4>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-danger shadow-sm">
              <CardContent>
                <div className="text-uppercase text-danger small mb-1">Credit Purchases</div>
                <h4 className="mb-0 text-danger">{formatAmount(summary.total_credit_purchases)}</h4>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card className="h-100 border-start border-success shadow-sm">
              <CardContent>
                <div className="text-uppercase text-success small mb-1">Total Payments</div>
                <h4 className="mb-0 text-success">{formatAmount(summary.total_payments)}</h4>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card className="mb-4 shadow-sm">
          <CardContent>
            <Grid container spacing={3} alignItems="end">
              <Grid item xs={12} sm={4} md={3}>
                <Label name="From Date" />
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Label name="To Date" />
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Label name="Search Transactions" />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Ref No, Description, Mode..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button fullWidth variant="contained" color="primary" onClick={fetchLedger}>
                  Refresh
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Ledger Table */}
        <MuiPaper>
          {loading && <LinearProgress />}

          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Ref No</th>
                  <th>Description</th>
                  <th>Mode</th>
                  <th className="text-end">Debit (Dr)</th>
                  <th className="text-end">Credit (Cr)</th>
                  <th className="text-end">Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredTxns.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      {loading ? "Loading transactions..." : "No transactions found for this period"}
                    </td>
                  </tr>
                ) : (
                  filteredTxns.map((txn, idx) => (
                    <tr
                      key={idx}
                      className={
                        txn.mode === "Opening"
                          ? "table-secondary fw-bold"
                          : txn.mode === "Cash"
                          ? "table-light"
                          : ""
                      }
                    >
                      <td>{txn.date}</td>
                      <td>
                        <span className="badge bg-primary">{txn.ref_no || "-"}</span>
                      </td>
                      <td>{txn.description}</td>
                      <td>
                        <span
                          className={`badge ${
                            txn.mode === "Credit"
                              ? "bg-danger"
                              : txn.mode === "Payment"
                              ? "bg-success"
                              : txn.mode === "Opening"
                              ? "bg-info"
                              : "bg-secondary"
                          }`}
                        >
                          {txn.mode}
                        </span>
                      </td>
                      <td className="text-end text-danger fw-bold">
                        {txn.debit > 0 ? formatAmount(txn.debit) : ""}
                      </td>
                      <td className="text-end text-success fw-bold">
                        {txn.credit > 0 ? formatAmount(txn.credit) : ""}
                      </td>
                      <td className="text-end fw-bold">
                        {formatAmount(txn.balance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </MuiPaper>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  snackbars: state.snackbars,
});

const mapDispatchToProps = (dispatch) => ({
  actions: {
    snackbarActions: bindActionCreators(snackbarActions, dispatch),
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SupplierLedgerDetailReport);